import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Divider,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import AdditionalImagesTab from './AdditionalImagesTab';
import StepTab from './StepTab';
import type { CallContentDto, ContentComment, InstructionStep } from '../types';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    height: '100%',
    overflow: 'hidden',
  },
  list: {
    width: '200px',
    minWidth: '200px',
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  navList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  listItem: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: tokens.fontSizeBase200,
    fontFamily: tokens.fontFamilyBase,
    border: 'none',
    backgroundColor: 'transparent',
    textAlign: 'start' as const,
    width: '100%',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  listItemActive: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    fontWeight: tokens.fontWeightSemibold,
  },
  detail: {
    flex: 1,
    overflowY: 'auto',
    padding: tokens.spacingHorizontalL,
  },
  stepSection: {
    paddingBottom: tokens.spacingVerticalXXL,
  },
  stepHeading: {
    marginTop: 0,
    marginBottom: tokens.spacingVerticalM,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    fontFamily: tokens.fontFamilyBase,
  },
});

interface InstructionsTabProps {
  steps: InstructionStep[];
  stepImages: Record<string, string[]>;
  allImageIds: string[];
  comments: ContentComment[];
  completedActionIds: number[];
  appendixImageIds: string[];
  domainId: number;
  isEditing?: boolean;
  saveSignal?: number;
  resetSignal?: number;
  callId?: number;
  contentId?: string;
  content?: CallContentDto;
}

export default function InstructionsTab({
  steps,
  stepImages,
  allImageIds,
  comments,
  completedActionIds,
  appendixImageIds,
  domainId,
  isEditing,
  saveSignal,
  resetSignal,
  callId,
  contentId,
  content,
}: InstructionsTabProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState<number | 'additional'>(0);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const additionalRef = useRef<HTMLDivElement | null>(null);
  const detailRef = useRef<HTMLDivElement | null>(null);

  const hasAppendix = appendixImageIds.length > 0;
  const totalSections = steps.length + (hasAppendix ? 1 : 0);

  const setSectionRef = useCallback((index: number) => (el: HTMLDivElement | null) => {
    sectionRefs.current[index] = el;
  }, []);

  // Track active section via IntersectionObserver
  useEffect(() => {
    const container = detailRef.current;
    if (!container) return;

    const allRefs = [...sectionRefs.current.slice(0, steps.length)];
    if (hasAppendix && additionalRef.current) allRefs.push(additionalRef.current);

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the topmost visible section
        let topEntry: IntersectionObserverEntry | null = null;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (!topEntry || entry.boundingClientRect.top < topEntry.boundingClientRect.top) {
              topEntry = entry;
            }
          }
        }
        if (topEntry) {
          const idx = allRefs.indexOf(topEntry.target as HTMLDivElement);
          if (idx >= 0) {
            setActiveIndex(idx < steps.length ? idx : 'additional');
          }
        }
      },
      { root: container, threshold: 0, rootMargin: '0px 0px -70% 0px' },
    );

    for (const ref of allRefs) {
      if (ref) observer.observe(ref);
    }

    return () => observer.disconnect();
  }, [steps.length, hasAppendix, totalSections]);

  function scrollTo(index: number | 'additional') {
    const el = index === 'additional'
      ? additionalRef.current
      : sectionRefs.current[index];
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div className={styles.root}>
      <nav className={styles.list} aria-label={t('instructions')}>
        <ul className={styles.navList}>
          {steps.map((step, index) => (
            <li key={step.id}>
              <button
                className={mergeClasses(
                  styles.listItem,
                  activeIndex === index && styles.listItemActive,
                )}
                onClick={() => scrollTo(index)}
                title={step.mainText || step.text}
              >
                {index + 1}. {step.mainText || step.text}
              </button>
            </li>
          ))}
          {hasAppendix && (
            <>
              <Divider />
              <li>
                <button
                  className={mergeClasses(
                    styles.listItem,
                    activeIndex === 'additional' && styles.listItemActive,
                  )}
                  onClick={() => scrollTo('additional')}
                >
                  {t('additionalImages')}
                </button>
              </li>
            </>
          )}
        </ul>
      </nav>
      <div ref={detailRef} className={styles.detail}>
        {steps.map((step, index) => (
          <div key={step.id} ref={setSectionRef(index)} className={styles.stepSection}>
            <h2 className={styles.stepHeading}>
              {index + 1}. {step.mainText || step.text}
            </h2>
            <StepTab
              step={step}
              stepImageIds={stepImages[step.id] ?? []}
              allImageIds={allImageIds}
              comment={comments.find(c => c.stepId === step.id)?.comment}
              completedActionIds={completedActionIds}
              domainId={domainId}
              isEditing={isEditing}
              saveSignal={saveSignal}
              resetSignal={resetSignal}
              callId={callId}
              contentId={contentId}
              content={content}
            />
            {index < steps.length - 1 && <Divider style={{ marginTop: tokens.spacingVerticalL }} />}
          </div>
        ))}
        {hasAppendix && (
          <div ref={additionalRef} className={styles.stepSection}>
            <Divider style={{ marginBottom: tokens.spacingVerticalL }} />
            <AdditionalImagesTab imageIds={appendixImageIds} domainId={domainId} />
          </div>
        )}
      </div>
    </div>
  );
}
